import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Star, AlertTriangle, DollarSign, Calendar, 
  Moon, Crown, Sprout, ArrowLeft, ChevronRight,
  Sparkles, RefreshCw, Info, TrendingUp, Mail
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Icon-Mapping für Segmente
const SEGMENT_ICONS = {
  stammgaeste: Star,
  abwanderungsgefaehrdet: AlertTriangle,
  preis_sensitiv: DollarSign,
  wochenend_besteller: Calendar,
  inaktive_gaeste: Moon,
  vip_kunden: Crown,
  neukunden: Sprout
};

// Farb-Mapping für Segmente
const SEGMENT_COLORS = {
  yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  red: 'bg-red-500/20 text-red-400 border-red-500/30',
  orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  gold: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  green: 'bg-green-500/20 text-green-400 border-green-500/30'
};

export default function GuestSegments() {
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState([]);
  const [totalGuests, setTotalGuests] = useState(0);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentGuests, setSegmentGuests] = useState([]);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [loadingSegment, setLoadingSegment] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchSegments = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/guests/segments`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSegments(data.segments || []);
        setTotalGuests(data.total_guests || 0);
      }
    } catch (error) {
      console.error('Segments fetch error:', error);
      toast.error('Fehler beim Laden der Segmente');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSegmentDetail = async (segmentKey) => {
    setLoadingSegment(true);
    try {
      const response = await fetch(`${API_BASE}/api/guests/segments/${segmentKey}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedSegment(data.segment);
        setSegmentGuests(data.guests || []);
        setShowSegmentModal(true);
      }
    } catch (error) {
      console.error('Segment detail fetch error:', error);
      toast.error('Fehler beim Laden der Segment-Details');
    } finally {
      setLoadingSegment(false);
    }
  };

  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const SegmentIcon = ({ segmentKey, className }) => {
    const IconComponent = SEGMENT_ICONS[segmentKey] || Users;
    return <IconComponent className={className} />;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]" data-testid="segments-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-400">Lade KI-Segmente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="guest-segments">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            KI-Segmente
          </h2>
          <p className="text-gray-400 mt-1">
            Automatische Gäste-Segmentierung ohne manuelles Filtern
          </p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {totalGuests} Gäste gesamt
        </Badge>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-purple-500/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Autonome Segmentierung</p>
              <p className="text-sm text-gray-300 mt-1">
                Die KI analysiert das Bestellverhalten Ihrer Gäste und ordnet sie automatisch 
                den passenden Segmenten zu. Jeder Gast kann in mehreren Segmenten sein.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Segment Grid */}
      {segments.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-white mb-2">Keine Gäste vorhanden</h3>
            <p className="text-gray-400">
              Synchronisieren Sie Ihre Bestellungen, um Gäste-Segmente zu erstellen
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {segments.map((segment, idx) => (
            <Card 
              key={segment.segment_key}
              className={`bg-slate-800/50 border-slate-700 cursor-pointer hover:border-slate-600 transition-all ${
                segment.guest_count > 0 ? 'hover:scale-[1.02]' : 'opacity-60'
              }`}
              onClick={() => segment.guest_count > 0 && fetchSegmentDetail(segment.segment_key)}
              data-testid={`segment-card-${segment.segment_key}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      SEGMENT_COLORS[segment.color] || SEGMENT_COLORS.gray
                    }`}>
                      <SegmentIcon segmentKey={segment.segment_key} className="w-6 h-6" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-white">{segment.name}</CardTitle>
                      <p className="text-2xl font-bold text-white">{segment.guest_count}</p>
                    </div>
                  </div>
                  {segment.guest_count > 0 && (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-400 mb-3">{segment.description}</p>
                
                {/* Preview Guests */}
                {segment.preview_guests?.length > 0 && (
                  <div className="space-y-2">
                    {segment.preview_guests.map((guest, gIdx) => (
                      <div 
                        key={guest.guest_id || gIdx}
                        className="flex items-center gap-2 text-sm bg-slate-900/50 p-2 rounded"
                      >
                        <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">
                          {guest.name?.charAt(0)}
                        </div>
                        <span className="text-white truncate flex-1">{guest.name}</span>
                      </div>
                    ))}
                    {segment.guest_count > 3 && (
                      <p className="text-xs text-gray-500 text-center">
                        + {segment.guest_count - 3} weitere
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Segment Detail Modal */}
      <Dialog open={showSegmentModal} onOpenChange={setShowSegmentModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedSegment && (
                <>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    SEGMENT_COLORS[selectedSegment.color] || SEGMENT_COLORS.gray
                  }`}>
                    <SegmentIcon segmentKey={selectedSegment.key} className="w-5 h-5" />
                  </div>
                  <div>
                    <span>{selectedSegment.name}</span>
                    <Badge variant="outline" className="ml-2">{segmentGuests.length} Gäste</Badge>
                  </div>
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedSegment?.description}
            </DialogDescription>
          </DialogHeader>
          
          {loadingSegment ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
            </div>
          ) : (
            <div className="space-y-3 mt-4">
              {segmentGuests.map((guest, idx) => (
                <Card 
                  key={guest.guest_id || idx}
                  className="bg-slate-800/50 border-slate-700"
                  data-testid={`segment-guest-${idx}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          guest.is_vip ? 'bg-yellow-500/20 text-yellow-400' :
                          guest.is_at_risk ? 'bg-red-500/20 text-red-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {guest.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-white">{guest.name}</h4>
                            {guest.is_vip && (
                              <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">VIP</Badge>
                            )}
                          </div>
                          
                          {/* KI-Zusammenfassung */}
                          <p className="text-sm text-purple-300 mb-2 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            {guest.ai_summary}
                          </p>
                          
                          {/* Segment-Begründung */}
                          <div className="bg-slate-900/50 p-2 rounded border-l-2 border-purple-500">
                            <p className="text-xs text-gray-400">
                              <strong className="text-purple-400">Warum in diesem Segment:</strong>
                            </p>
                            <p className="text-sm text-white mt-1">{guest.segment_reason}</p>
                          </div>
                          
                          {/* Weitere Segmente */}
                          {guest.segments?.length > 1 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="text-xs text-gray-500">Weitere Segmente:</span>
                              {guest.segments.filter(s => s.segment_key !== selectedSegment?.key).map((seg, sIdx) => (
                                <Badge 
                                  key={sIdx} 
                                  variant="outline" 
                                  className={`text-xs ${SEGMENT_COLORS[seg.color]}`}
                                >
                                  {seg.icon} {seg.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Metriken */}
                      <div className="text-right text-sm">
                        <p className="font-bold text-white">{formatCurrency(guest.total_spent)}</p>
                        <p className="text-gray-400">{guest.total_orders} Bestellungen</p>
                        <p className="text-gray-500 text-xs">Ø {formatCurrency(guest.average_order_value)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* Aktionen */}
          <div className="flex justify-between items-center pt-4 border-t border-slate-700">
            <p className="text-sm text-gray-400">
              <TrendingUp className="w-4 h-4 inline mr-1" />
              Gesamtumsatz: {formatCurrency(segmentGuests.reduce((sum, g) => sum + (g.total_spent || 0), 0))}
            </p>
            <Button variant="outline" className="gap-2">
              <Mail className="w-4 h-4" />
              Kampagne starten
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
